import React, { useState, useEffect, useMemo } from "react";
import UserDropdown from "../../../components/AdminDropDown/UserDropdown";
import { MdNotifications, MdMenu, MdMessage } from "react-icons/md";
import Logo2 from "../../../assets/new images/logo.png";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import apiKey from "../../../config/apiKey";
import Pusher from "pusher-js";
import Echo from "laravel-echo";
import LanguageSwitcher from "../../../components/LanguageSwitcher";

const TopBar = ({ toggleSidebar, totalUnreadCount }) => {
  const { t } = useTranslation();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [formUser, setFormUser] = useState({});
  const [notifications, setNotifications] = useState([]);

  const teacher = JSON.parse(localStorage.getItem("edo-teacher"));
  const userId = teacher ? teacher.teacher.id : "";

  const user = useMemo(
    () => ({
      name: formUser.name,
      admin: "Teacher",
      id: teacher ? teacher.teacher.id : "",
    }),
    [formUser, teacher]
  );

  // --- Fetch Messages ---
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axios.get(
          `${apiKey}/api/edolutions/teacher/showmessage/${userId}`,
          { headers: { Authorization: `Bearer ${teacher?.token}` } }
        );
        let totalUnread = 0;
        res.data.messages_by_student.forEach((student) => {
          const unread = student.messages.filter(
            (msg) => msg.read === 0 && msg.student_message !== null
          ).length;
          totalUnread += unread;
        });
        setMessageCount(totalUnread);
      } catch (err) {
        console.error("Error fetching messages:", err);
      }
    };
    if (userId) fetchMessages();
  }, [userId, teacher?.token]);

  // --- Fetch User Details ---
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await axios.get(
          `${apiKey}/api/edolutions/teacher/getuser/${userId}`,
          { headers: { Authorization: `Bearer ${teacher?.token}` } }
        );
        setFormUser(res.data);
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };
    if (userId) fetchUserDetails();
  }, [userId, teacher?.token]);

  // --- Fetch Notifications ---
  const fetchNotifications = async () => {
    try {
      const [unreadCountRes, notificationsRes] = await Promise.all([
        axios.get(
          `${apiKey}/api/edolutions/teacher/notifications/unread-count/${user.id}`,
          { headers: { Authorization: `Bearer ${teacher?.token}` } }
        ),
        axios.get(`${apiKey}/api/edolutions/teacher/notifications/${user.id}`, {
          headers: { Authorization: `Bearer ${teacher?.token}` },
        }),
      ]);
      setNotificationCount(unreadCountRes.data.unreadCount);
      setNotifications(notificationsRes.data);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  // --- Laravel Echo (Realtime) ---
  useEffect(() => {
    if (teacher?.token && userId) {
      window.Pusher = Pusher;
      window.Echo = new Echo({
        broadcaster: "pusher",
        key: "301d873c1e8e09e9534c",
        cluster: "ap2",
        forceTLS: true,
        encrypted: true,
        authEndpoint: `${apiKey}/broadcasting/auth`,
        auth: {
          headers: { Authorization: `Bearer ${teacher?.token}` },
        },
      });

      const notifCh = window.Echo.private(
        `notification-count-channel.teacher.${userId}`
      );
      notifCh.listen("NotificationCountEvent", (e) =>
        setNotificationCount(e.data)
      );

      const msgCh = window.Echo.private(
        `chat-message-count-event.teacher.${userId}`
      );
      msgCh.listen("ChatMessageCountTeacherEvent", (e) =>
        setMessageCount(e.data)
      );
    }
  }, [teacher?.token, userId]);

  useEffect(() => {
    if (user.id) fetchNotifications();
  }, [user.id]);

  // --- Mark all notifications as read ---
  const markAllCount = async () => {
    try {
      const markAllPromises = notifications
        .map((n) =>
          !n.count
            ? axios.put(
                `${apiKey}/api/edolutions/teacher/notifications/markcount/${n.id}`,
                {},
                { headers: { Authorization: `Bearer ${teacher?.token}` } }
              )
            : null
        )
        .filter(Boolean);

      await Promise.all(markAllPromises);
      setNotifications((prev) => prev.map((n) => ({ ...n, count: true })));
      setNotificationCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // --- Responsive menu icon ---
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1280);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dropdown = [{ path: "./mentor-setting" }];
  const totalMsgs = totalUnreadCount > 0 ? totalUnreadCount : messageCount;

  return (
    <header className="sticky top-0 z-30">
      {/* Gradient Brand Ribbon */}
      <div className="h-1 w-full bg-gradient-to-r from-[#EE5F2B] via-[#2A3A54] to-[#EE5F2B]" />

      {/* Glass Header */}
      <div className="backdrop-blur bg-white/80 shadow-sm border-b border-black/5">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Left Side */}
          <div className="flex items-center gap-3">
            {isSmallScreen && (
              <button
                onClick={toggleSidebar}
                className="inline-flex items-center justify-center rounded-xl border border-black/10 bg-white/70 hover:bg-white/90 active:scale-[0.98] transition px-2.5 py-2"
                aria-label={t("teacherDash.topBar.toggleSidebar")}
              >
                <MdMenu className="text-[#2A3A54] w-6 h-6" />
              </button>
            )}

            <Link to="./home-profile" className="hidden md:block">
              <img
                src={Logo2}
                alt="Edolutions"
                className="h-9 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Messages */}
            {/* <Link to="./mentorChat" className="relative group">
              <button
                className="inline-flex items-center gap-2 rounded-xl border bg-white/80 border-black/10 hover:border-black/20 hover:bg-white transition px-3 py-2"
                aria-label="Messages"
              >
                <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] text-white">
                  <MdMessage className="w-4 h-4" />
                </span>
                <span className="hidden sm:block text-sm font-semibold text-[#2A3A54]">
                  Messages
                </span>
              </button>
              {totalMsgs > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white grid place-items-center bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] shadow"
                  aria-label={`${totalMsgs} unread messages`}
                >
                  {totalMsgs}
                </span>
              )}
            </Link> */}

            {/* Notifications */}
            <div className="relative group" onClick={markAllCount}>
              <Link to="./notification">
                <button
                  className="inline-flex items-center gap-2 rounded-xl border bg-white/80 border-black/10 hover:border-black/20 hover:bg-white transition px-3 py-2"
                  aria-label={t("teacherDash.topBar.notifications")}
                >
                  <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] text-white">
                    <MdNotifications className="w-4 h-4" />
                  </span>
                  <span className="hidden sm:block text-sm font-semibold text-[#2A3A54]">
                    {t("teacherDash.topBar.alerts")}
                  </span>
                </button>
              </Link>
              {notificationCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white grid place-items-center bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] shadow"
                  aria-label={t("teacherDash.topBar.unreadNotifications", { count: notificationCount })}
                >
                  {notificationCount}
                </span>
              )}
            </div>

            <LanguageSwitcher variant="compact" />

            {/* User Dropdown */}
            <UserDropdown user={user} dropdown={dropdown} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
